// De lokale kant: een kopie van je notities in de browser, en een wachtrij met wat er nog
// naar de server moet.
//
// Dit is de belofte "wat je typt gaat nooit verloren" uit het plan. Alles wat je schrijft
// gaat eerst hierheen en pas daarna het netwerk op. In O1 is dat bewust eenvoudig: de
// wachtrij wordt op volgorde afgewerkt en bij een botsing maken we een conflictkopie. De
// echte samenvoeging komt in O3.

import { openDB, type IDBPDatabase } from "idb";
import { api, Botsing, GeenVerbinding, NietAangemeld, NietGevonden, type Blok, type Notitie, type Rij, type Taak, type Treffer } from "./api.ts";
import { fragmentVan, naarTekst } from "./tekst.ts";
// Uit de serverkant, net als de markdown: het nummer van een taak is de plek die hij in de
// notitie inneemt, en als dit apparaat anders telt dan de NAS zet je straks het verkeerde
// vinkje. Dezelfde functie voor allebei is hier dus geen netheid maar noodzaak.
import { takenUit } from "../../server/kern/inhoud.ts";
import { leesWanneer } from "../../server/kern/wanneer.ts";
import { voegSamen, voegTekstSamen } from "./samenvoegen.ts";

export type WachtrijItem = {
  nr?: number;
  soort: "maak" | "wijzig" | "verwijder" | "herstel";
  id: string;
  lading?: Record<string, unknown>;
  rev?: number;
  tijd: string;
  pogingen?: number;
};

export type SyncStand = {
  bezig: boolean;
  wachtend: number;
  offline: boolean;
  afgemeld: boolean;
  laatsteFout: string | null;
  /** Iets goeds om te weten — nu: dat twee versies zijn samengevoegd. */
  laatsteMelding: string | null;
};

let dbBelofte: Promise<IDBPDatabase> | null = null;

function db(): Promise<IDBPDatabase> {
  if (!dbBelofte) {
    dbBelofte = openDB("notities", 2, {
      upgrade(db, vanaf) {
        if (vanaf < 1) {
          db.createObjectStore("notities", { keyPath: "id" });
          db.createObjectStore("rijen");
          db.createObjectStore("wachtrij", { keyPath: "nr", autoIncrement: true });
          db.createObjectStore("instellingen");
        }
        if (vanaf < 2) {
          // "notities" is wat jij ziet — inclusief wat je net hebt getypt en nog niet is
          // verstuurd. "basis" is de laatste stand die van de server kwam. Met die twee naast
          // elkaar valt straks uit te rekenen wat jij veranderde en wat het andere apparaat
          // deed, in plaats van er een conflictkopie van te maken.
          db.createObjectStore("basis", { keyPath: "id" });
        }
      }
    });
  }
  return dbBelofte;
}

// --- cache ------------------------------------------------------------------------------

export async function bewaarRijen(sleutel: string, rijen: Rij[]): Promise<void> {
  try {
    (await db()).put("rijen", rijen, sleutel);
  } catch {
    // Een volle of geblokkeerde IndexedDB mag de app nooit stilzetten; de server blijft leidend.
  }
}

export async function haalRijen(sleutel: string): Promise<Rij[] | null> {
  try {
    return ((await db()).get("rijen", sleutel) as Promise<Rij[] | undefined>).then((r) => r ?? null);
  } catch {
    return null;
  }
}

const notitieLuisteraars = new Set<(notitie: Notitie) => void>();

/**
 * De hoogste rev die we van een notitie kennen. Staat hier in het geheugen en niet alleen in
 * IndexedDB, omdat het versturen hier gebeurt: tussen "wijziging in de wachtrij zetten" en
 * "wijziging versturen" kan de server al een nieuwere rev hebben teruggegeven, en dan is de
 * rev die bij het bericht hoort verouderd voordat hij ooit de deur uit gaat.
 */
const laatsteRev = new Map<string, number>();

/** Zeggen wanneer de server een nieuwere versie teruggaf, zodat het scherm zijn rev bijwerkt. */
export function opNotitie(luisteraar: (notitie: Notitie) => void): () => void {
  notitieLuisteraars.add(luisteraar);
  return () => notitieLuisteraars.delete(luisteraar);
}

/**
 * Zoals de server hem teruggaf: dit is de waarheid van dat moment. Gaat naar de kopie die je
 * ziet én naar de basis waartegen we later kunnen vergelijken.
 */
export async function bewaarNotitie(notitie: Notitie): Promise<void> {
  if (notitie.rev >= (laatsteRev.get(notitie.id) ?? 0)) laatsteRev.set(notitie.id, notitie.rev);
  for (const luisteraar of notitieLuisteraars) luisteraar(notitie);
  try {
    const database = await db();
    await Promise.all([database.put("notities", notitie), database.put("basis", notitie)]);
  } catch {
    /* zie boven */
  }
}

/**
 * Wat jij net hebt getypt. Alleen naar de kopie die je ziet: de basis blijft staan zoals de
 * server hem gaf, anders is er straks niets meer om mee te vergelijken.
 */
export async function bewaarLokaal(notitie: Notitie): Promise<void> {
  // Geen luisteraars: wie dit schrijft heeft het scherm zelf al bijgewerkt, en anders zouden
  // de tags die de server uitrekent overschreven worden door de oude die hier nog stonden.
  try {
    await (await db()).put("notities", notitie);
  } catch {
    /* zie boven */
  }
}

/** De laatste stand die van de server kwam — de basis voor het samenvoegen in een latere stap. */
export async function haalBasis(id: string): Promise<Notitie | null> {
  try {
    return (await (await db()).get("basis", id)) ?? null;
  } catch {
    return null;
  }
}

export async function haalNotitieUitCache(id: string): Promise<Notitie | null> {
  try {
    return (await (await db()).get("notities", id)) ?? null;
  } catch {
    return null;
  }
}

export async function vergeetNotitie(id: string): Promise<void> {
  try {
    const database = await db();
    await Promise.all([database.delete("notities", id), database.delete("basis", id)]);
  } catch {
    /* zie boven */
  }
}

// --- de spiegel -------------------------------------------------------------------------
//
// Alle notities op dit apparaat, zodat lezen en zoeken ook werken zonder netwerk. Eén keer
// helemaal ophalen, daarna bijhouden met de wijzigingenlijst — dus niet elke keer opnieuw de
// hele boel binnentrekken.

export type SpiegelStand = { gevuld: boolean; volgnr: number; tijd: string };

export async function spiegelStand(): Promise<SpiegelStand | null> {
  try {
    return (await (await db()).get("instellingen", "spiegel")) ?? null;
  } catch {
    return null;
  }
}

export async function bewaarSpiegelStand(volgnr: number): Promise<void> {
  try {
    await (await db()).put("instellingen", { gevuld: true, volgnr, tijd: new Date().toISOString() }, "spiegel");
  } catch {
    /* zonder IndexedDB halen we de volgende keer gewoon alles opnieuw op */
  }
}

/**
 * Haal alles op, in stukken. Geeft het volgnummer terug waar de wijzigingenlijst vanaf verder
 * moet kijken — dat nummer komt van vóór het ophalen, dus wat er tijdens het ophalen
 * veranderde komt er daarna alsnog achteraan.
 */
export async function vulSpiegel(stuk = 200): Promise<{ notities: number; volgnr: number } | null> {
  try {
    const database = await db();
    let na: string | undefined;
    let volgnr = 0;
    let aantal = 0;

    for (let ronde = 0; ronde < 200; ronde++) {
      const antwoord = await api.spiegel(na, stuk);
      if (ronde === 0) volgnr = antwoord.volgnr;
      // Wat hier nog klaarstaat om verstuurd te worden is nieuwer dan wat de server vertelt;
      // dat mag de spiegel niet overschrijven.
      const wacht = await wachtrijIds();
      for (const notitie of antwoord.notities) {
        if (notitie.rev >= (laatsteRev.get(notitie.id) ?? 0)) laatsteRev.set(notitie.id, notitie.rev);
        await database.put("basis", notitie);
        if (!wacht.has(notitie.id)) await database.put("notities", notitie);
      }
      aantal += antwoord.notities.length;
      if (antwoord.klaar || antwoord.notities.length === 0) break;
      na = antwoord.notities[antwoord.notities.length - 1].id;
    }

    await bewaarSpiegelStand(volgnr);
    return { notities: aantal, volgnr };
  } catch {
    // Geen verbinding of niet aangemeld: dan blijft de spiegel staan zoals hij stond en
    // proberen we het bij de volgende start opnieuw.
    return null;
  }
}

/** Zoals de server hem zou sturen, maar dan uit de kopie op dit apparaat. */
function alsRij(notitie: Notitie): Rij {
  return {
    id: notitie.id,
    titel: notitie.titel,
    fragment: fragmentVan(notitie.inhoud),
    eigenschappen: notitie.eigenschappen ?? {},
    kluis: !!notitie.kluis,
    gepind: notitie.gepind,
    kleur: notitie.kleur,
    ouder: notitie.ouder,
    gewijzigd: notitie.gewijzigd,
    aangemaakt: notitie.aangemaakt,
    taken: notitie.taken,
    tags: notitie.tags ?? []
  };
}

function hoortErbij(notitie: Notitie, opties: { boek?: string; tag?: string }): boolean {
  if (notitie.soort !== "notitie" || notitie.verwijderd || notitie.gearchiveerd || notitie.sjabloon) return false;
  if (opties.boek && notitie.ouder !== opties.boek) return false;
  if (opties.tag) {
    const zoek = opties.tag.toLowerCase();
    // Een tag omvat zijn geneste kinderen, net als op de server: #klant vindt ook #klant/eijsink.
    if (!(notitie.tags ?? []).some((t) => t.toLowerCase() === zoek || t.toLowerCase().startsWith(`${zoek}/`))) return false;
  }
  return true;
}

/** De lijst uit de eigen kopie, met dezelfde volgorde als de server hem zou geven. */
export async function lijstUitSpiegel(
  opties: { boek?: string; tag?: string; sorteer?: string; limiet?: number } = {}
): Promise<Rij[]> {
  try {
    const alles = (await (await db()).getAll("notities")) as Notitie[];
    const rijen = alles.filter((n) => hoortErbij(n, opties));
    const hoe = opties.sorteer ?? "gewijzigd";
    rijen.sort((a, b) => {
      if (hoe === "titel") return a.titel.localeCompare(b.titel, "nl", { sensitivity: "base" });
      if (hoe === "aangemaakt") return b.aangemaakt.localeCompare(a.aangemaakt);
      return b.gewijzigd.localeCompare(a.gewijzigd);
    });
    return rijen.slice(0, opties.limiet ?? 200).map(alsRij);
  } catch {
    return [];
  }
}

/**
 * Zoeken in de eigen kopie. Eenvoudiger dan de server: losse woorden die állemaal moeten
 * voorkomen in titel of tekst. Geen relevantie, geen stamwoorden — genoeg om zonder netwerk
 * te vinden wat je zoekt, en het zegt er in het scherm bij dat je offline zoekt.
 */
export async function zoekInSpiegel(vraag: string, limiet = 50): Promise<Treffer[]> {
  const woorden = vraag.toLowerCase().split(/\s+/).filter((w) => w.length > 0);
  if (woorden.length === 0) return [];
  try {
    const alles = (await (await db()).getAll("notities")) as Notitie[];
    const treffers: Treffer[] = [];
    for (const notitie of alles) {
      if (notitie.soort !== "notitie" || notitie.verwijderd) continue;
      const tekst = naarTekst(notitie.inhoud);
      const hooiberg = `${notitie.titel}\n${tekst}`.toLowerCase();
      if (!woorden.every((w) => hooiberg.includes(w))) continue;

      // Het fragment begint bij het eerste gevonden woord, zodat je ziet wáárom het een
      // treffer is — net als bij het zoeken op de server.
      const plek = tekst.toLowerCase().indexOf(woorden[0]);
      const vanaf = plek < 0 ? 0 : Math.max(0, plek - 40);
      treffers.push({
        ...alsRij(notitie),
        fragment: (vanaf > 0 ? "…" : "") + tekst.slice(vanaf, vanaf + 160).replace(/\s+/g, " ").trim(),
        score: 0
      });
      if (treffers.length >= limiet) break;
    }
    return treffers;
  } catch {
    return [];
  }
}

/**
 * Het takenoverzicht uit de eigen kopie, voor als de server niet bereikbaar is.
 *
 * Dezelfde volgorde en dezelfde uitsluitingen als op de server, zodat de lijst niet verspringt
 * zodra je weer verbinding hebt. De nummering komt uit dezelfde functie als daar — die staat in
 * server/kern/inhoud.ts en wordt door allebei gebruikt.
 */
export async function takenUitSpiegel(af?: boolean, limiet = 200): Promise<Taak[]> {
  try {
    const alles = (await (await db()).getAll("notities")) as Notitie[];
    const uit: Taak[] = [];
    for (const notitie of alles) {
      if (!hoortErbij(notitie, {})) continue;
      for (const regel of takenUit(notitie.inhoud)) {
        if (regel.tekst === "") continue;
        if (af !== undefined && regel.af !== af) continue;
        // Dezelfde lezer als op de server, anders staat er straks een andere datum bij.
        const gelezen = leesWanneer(regel.tekst);
        uit.push({
          notitie: notitie.id,
          titel: notitie.titel,
          nr: regel.nr,
          tekst: gelezen.tekst,
          af: regel.af,
          diepte: regel.diepte,
          wanneer: gelezen.wanneer,
          gewijzigd: notitie.gewijzigd,
          boek: notitie.ouder ?? null
        });
      }
    }
    // Dezelfde volgorde als de server: eerst wat een datum heeft, dan de rest.
    uit.sort((a, b) => {
      if (a.wanneer && b.wanneer) return a.wanneer.localeCompare(b.wanneer);
      if (a.wanneer) return -1;
      if (b.wanneer) return 1;
      return a.gewijzigd === b.gewijzigd ? a.nr - b.nr : b.gewijzigd.localeCompare(a.gewijzigd);
    });
    return uit.slice(0, limiet);
  } catch {
    return [];
  }
}

export async function alleNotitiesUitCache(): Promise<Notitie[]> {
  try {
    return await (await db()).getAll("notities");
  } catch {
    return [];
  }
}

// --- wachtrij ---------------------------------------------------------------------------

const luisteraars = new Set<(stand: SyncStand) => void>();
let stand: SyncStand = { bezig: false, wachtend: 0, offline: false, afgemeld: false, laatsteFout: null, laatsteMelding: null };

export function opSync(luisteraar: (stand: SyncStand) => void): () => void {
  luisteraars.add(luisteraar);
  luisteraar(stand);
  return () => luisteraars.delete(luisteraar);
}

function meld(nieuw: Partial<SyncStand>): void {
  stand = { ...stand, ...nieuw };
  for (const luisteraar of luisteraars) luisteraar(stand);
}

async function tel(): Promise<number> {
  try {
    return await (await db()).count("wachtrij");
  } catch {
    return 0;
  }
}

/**
 * Van welke notities staat hier nog iets klaar? Wat de server over díe notities vertelt is
 * ouder dan wat jij hebt getypt, dus dat mag de kopie op dit apparaat niet overschrijven.
 */
export async function wachtrijIds(): Promise<Set<string>> {
  try {
    const items = (await (await db()).getAll("wachtrij")) as WachtrijItem[];
    return new Set(items.map((i) => i.id));
  } catch {
    return new Set();
  }
}

export async function zetInWachtrij(item: WachtrijItem): Promise<void> {
  const database = await db();
  if (item.soort === "wijzig") {
    const bestaande = (await database.getAll("wachtrij")) as WachtrijItem[];

    // Staat de notitie zelf nog in de wachtrij? Dan hoort de wijziging daarin thuis en niet
    // als los bericht erachter. Anders stuurt de browser een wijziging voor een notitie die
    // de server nog niet kent, krijgt een 404, en blijft de hele wachtrij daarop staan —
    // precies wat er bij de eerste proef op de laptop gebeurde.
    // Niet bijschrijven op een bericht dat op dit moment de deur uit is: dat is al verstuurd
    // zoals het was, en wordt daarna weggegooid — met jouw bijschrijving erin. Dan blijft die
    // titel wél op dit apparaat staan en komt hij nooit op de server. Zolang het aanmaken
    // onderweg is, gaat de wijziging er gewoon als los bericht achteraan.
    const nogTeMaken = bestaande.find((b) => b.soort === "maak" && b.id === item.id && b.nr !== onderweg);
    if (nogTeMaken?.nr !== undefined) {
      await database.put("wachtrij", {
        ...nogTeMaken,
        lading: { ...(nogTeMaken.lading ?? {}), ...(item.lading ?? {}) }
      });
      meld({ wachtend: await tel() });
      void verwerkWachtrij();
      return;
    }

    // Een tweede wijziging aan dezelfde notitie gaat op in de eerste: alleen de laatste stand
    // hoeft naar de server. Zonder dit staan er na een minuut typen tientallen berichten klaar.
    //
    // Samenvoegen en niet vervangen: een bericht draagt alleen de velden die veranderd zijn.
    // Verving de nieuwe het oude helemaal, dan verdween een titelwijziging zodra je daarna in
    // de tekst typte — de titel stond dan nog wel op dit apparaat, maar nooit op de server.
    let samen = { ...(item.lading ?? {}) };
    for (const oud of bestaande) {
      if (oud.soort === "wijzig" && oud.id === item.id && oud.nr !== undefined) {
        samen = { ...(oud.lading ?? {}), ...samen };
        await database.delete("wachtrij", oud.nr);
      }
    }
    item = { ...item, lading: samen };
  }
  await database.add("wachtrij", { ...item, tijd: new Date().toISOString() });
  meld({ wachtend: await tel() });
  void verwerkWachtrij();
}

let lopend: Promise<void> | null = null;
/** Het bericht dat op dit moment bij de server ligt; daar mag niets meer bij geschreven worden. */
let onderweg: number | null = null;

/**
 * Werkt de wachtrij af, oudste eerst. Stopt bij het eerste netwerkprobleem.
 *
 * Loopt er al een ronde, dan krijg je díe belofte terug en niet meteen een lege. Wie
 * `await verwerkWachtrij()` schrijft, bedoelt "wacht tot het verstuurd is" — en kreeg
 * voorheen direct antwoord als er toevallig al een ronde bezig was.
 */
export function verwerkWachtrij(): Promise<void> {
  if (lopend) return lopend;
  lopend = ronde().finally(() => {
    lopend = null;
  });
  return lopend;
}

async function ronde(): Promise<void> {
  meld({ bezig: true });
  try {
    const database = await db();
    for (;;) {
      const items = (await database.getAll("wachtrij", undefined, 1)) as WachtrijItem[];
      if (items.length === 0) break;
      const item = items[0];
      onderweg = item.nr ?? null;
      try {
        await voerUit(item);
        await database.delete("wachtrij", item.nr!);
        meld({ offline: false, afgemeld: false, laatsteFout: null, wachtend: await tel() });
      } catch (fout) {
        if (fout instanceof GeenVerbinding) {
          meld({ offline: true });
          break;
        }
        if (fout instanceof NietAangemeld) {
          meld({ afgemeld: true });
          break;
        }
        if (fout instanceof Botsing) {
          // Eerst proberen samen te voegen: raakten jij en het andere apparaat verschillende
          // stukken, dan kunnen beide wijzigingen gewoon blijven staan. Lukt dat niet, dan
          // pas een kopie ernaast — dat is eerlijker dan één van de twee overschrijven.
          const samen = await probeerSamenTeVoegen(item, fout.actueel);
          if (!samen) await maakConflictkopie(item, fout.actueel);
          await database.delete("wachtrij", item.nr!);
          continue;
        }
        if (fout instanceof NietGevonden) {
          // De notitie bestaat niet (meer) op de server: hem eindeloos opnieuw aanbieden
          // houdt alles erachter tegen. Weggooien en door.
          await database.delete("wachtrij", item.nr!);
          continue;
        }
        // Onbekende fout: een paar keer opnieuw proberen en daarna laten liggen met een
        // melding, in plaats van de hele wachtrij eindeloos te blokkeren.
        const pogingen = (item.pogingen ?? 0) + 1;
        if (pogingen >= 5) {
          await database.delete("wachtrij", item.nr!);
          meld({ laatsteFout: `Kon een wijziging niet opslaan: ${(fout as Error).message}` });
        } else {
          await database.put("wachtrij", { ...item, pogingen });
          meld({ laatsteFout: (fout as Error).message });
          break;
        }
      } finally {
        onderweg = null;
      }
    }
  } finally {
    meld({ bezig: false, wachtend: await tel() });
  }
}

async function voerUit(item: WachtrijItem): Promise<void> {
  if (item.soort === "maak") {
    const notitie = await api.maak({ id: item.id, ...(item.lading as any) });
    await bewaarNotitie(notitie);
    return;
  }
  if (item.soort === "wijzig") {
    // De rev van vlak vóór het versturen, niet die van het moment dat de wijziging in de
    // wachtrij kwam. Typ je door terwijl er nog een wijziging onderweg is, dan geeft de
    // server ondertussen een nieuwere rev terug en is de meegegeven rev al verouderd als hij
    // aan de beurt is. De server ziet dan een botsing en de app maakt een conflictkopie —
    // van je eigen notitie, op je eigen telefoon. Drie regels typen leverde zo drie notities op.
    const rev = laatsteRev.get(item.id) ?? item.rev;
    const notitie = await api.wijzig(item.id, item.lading ?? {}, rev);
    await bewaarNotitie(notitie);
    return;
  }
  if (item.soort === "verwijder") {
    await api.verwijder(item.id);
    await vergeetNotitie(item.id);
    return;
  }
  if (item.soort === "herstel") {
    const notitie = await api.herstel(item.id);
    await bewaarNotitie(notitie);
  }
}

/**
 * Een ander apparaat was eerder, maar jullie zaten niet in hetzelfde stuk tekst. Dan hoeft er
 * niets gekozen te worden: we rekenen uit wat ieder veranderde ten opzichte van de laatste
 * gezamenlijke stand en sturen het resultaat als gewone wijziging terug.
 *
 * Geeft `false` als er wél een echte botsing in zit — dan volgt alsnog de conflictkopie.
 */
async function probeerSamenTeVoegen(item: WachtrijItem, actueel: Notitie): Promise<boolean> {
  if (item.soort !== "wijzig") return false;
  const basis = await haalBasis(item.id);
  const mijn = await haalNotitieUitCache(item.id);
  if (!basis || !mijn) return false;

  let tegenover = actueel;
  for (let poging = 0; poging < 3; poging++) {
    const titel = voegTekstSamen(basis.titel, mijn.titel, tegenover.titel);
    const inhoud = voegSamen(basis.inhoud, mijn.inhoud, tegenover.inhoud);
    if (!titel.schoon || !inhoud.schoon) return false;

    try {
      const notitie = await api.wijzig(
        item.id,
        { ...(item.lading ?? {}), titel: titel.tekst, inhoud: inhoud.doc },
        tegenover.rev
      );
      await bewaarNotitie(notitie);
      meld({
        laatsteMelding: `"${notitie.titel || "Zonder titel"}" was op twee apparaten bewerkt. Beide wijzigingen staan er nu in.`
      });
      return true;
    } catch (fout) {
      // Er kwam ondertussen alwéér iets binnen: opnieuw rekenen tegen die nieuwere stand.
      if (fout instanceof Botsing) {
        tegenover = fout.actueel;
        continue;
      }
      return false;
    }
  }
  return false;
}

/**
 * Wel een echte botsing: jullie schreven in hetzelfde stuk iets anders. Dan kiezen we niet,
 * maar zetten we jouw versie als notitie ernaast, zodat er niets verdwijnt.
 */
async function maakConflictkopie(item: WachtrijItem, actueel: Notitie): Promise<void> {
  const mijn = (item.lading ?? {}) as { titel?: string; inhoud?: Blok | null };
  const kopie = await api.maak({
    titel: `${mijn.titel ?? actueel.titel} (conflictkopie)`,
    inhoud: mijn.inhoud ?? actueel.inhoud,
    boek: actueel.ouder
  });
  await bewaarNotitie(kopie);
  await bewaarNotitie(actueel);
  meld({
    laatsteFout:
      "Deze notitie was ondertussen op een ander apparaat gewijzigd. Jouw versie staat nu als conflictkopie ernaast."
  });
}

/** De wachtrij opnieuw proberen zodra dat zin heeft. */
export function volgHetNetwerk(): void {
  addEventListener("online", () => void verwerkWachtrij());
  addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible") void verwerkWachtrij();
  });
  // Background Sync bestaat alleen in Chromium, dus een eigen klopje erbij: elke halve
  // minuut kijken of er nog iets klaarstaat.
  setInterval(() => void verwerkWachtrij(), 30_000);
}
